import type { ValidationApiItem, NormalizedAudio } from './types';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const toStringValue = (value: unknown): string | undefined => {
  if (typeof value === 'string' && value.trim()) return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return undefined;
};

export const normalizeAudioItem = (item: ValidationApiItem): NormalizedAudio | null => {
  const id = toStringValue(item.id ?? item._id ?? item.audioId);
  const word = toStringValue(item.phraseLocal ?? item.word ?? item.text ?? item.phrase) ?? '';
  const audioUrl = toStringValue(item.url);

  if (!id || !audioUrl) return null;

  const validatedBy = Array.isArray(item.validatedBy) ? item.validatedBy.map(String) : [];
  const rejectedBy = Array.isArray(item.rejectedBy) ? item.rejectedBy.map(String) : [];
  const score = typeof item.score === 'number' ? item.score : null;
  const transcription = Array.isArray(item.transcription) ? item.transcription : [];

  let recordingDurationMs: number | undefined;
  if (typeof item.recordingDurationMs === 'number') {
    recordingDurationMs = item.recordingDurationMs;
  } else if (typeof item.recordingDurationMs === 'string') {
    const parsed = parseInt(item.recordingDurationMs.trim(), 10);
    recordingDurationMs = Number.isFinite(parsed) ? parsed : undefined;
  }

  const uploadTiming = isRecord(item.uploadTiming)
    ? {
        startTime: String(item.uploadTiming.startTime ?? ''),
        endTime: String(item.uploadTiming.endTime ?? ''),
        durationMs: typeof item.uploadTiming.durationMs === 'number' ? item.uploadTiming.durationMs : 0,
        durationSeconds: String(item.uploadTiming.durationSeconds ?? '0'),
      }
    : undefined;

  return {
    id,
    word,
    audioUrl,
    audioGender: toStringValue(item.audioGender),
    phraseLocal: toStringValue(item.phraseLocal),
    phraseFr: toStringValue(item.phraseFr),
    lang: toStringValue(item.lang),
    blobName: toStringValue(item.blobName),
    validatedBy,
    rejectedBy,
    hasValidated: typeof item.hasValidated === 'boolean' ? item.hasValidated : false,
    hasRejected: typeof item.hasRejected === 'boolean' ? item.hasRejected : false,
    status: toStringValue(item.status),
    score,
    transcription,
    uploadedAt: toStringValue(item.uploadedAt),
    recordingDurationMs,
    source: toStringValue(item.source),
    audioRole: toStringValue(item.audioRole),
    uploadTiming,
  };
};

export const normalizeItems = (items: ValidationApiItem[]): NormalizedAudio[] =>
  items.map(normalizeAudioItem).filter((item): item is NormalizedAudio => item !== null);

export const filterAcceptedAudios = (audios: NormalizedAudio[]): NormalizedAudio[] =>
  audios.filter((audio) => !audio.hasValidated && !audio.hasRejected);

export const extractErrorMessage = (error: unknown): string | undefined => {
  if (error instanceof Error && error.message) return error.message;

  if (isRecord(error)) {
    if (typeof error.message === 'string' && error.message.trim()) return error.message;

    const response = error.response;
    if (isRecord(response)) {
      const data = response.data;
      if (isRecord(data) && typeof data.message === 'string' && data.message.trim()) {
        return data.message;
      }
    }
  }

  return undefined;
};

export const mapLanguageToCode = (value: string): string => {
  const normalize = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const v = normalize((value || '').toLowerCase());
  if (v === 'francais' || v === 'fr') return 'fr';
  const alias: Record<string, string> = {
    ewe: 'ewe',
    mina: 'mina',
    kabye: 'kabye',
    kabyee: 'kabye',
    kabyeh: 'kabye',
    kotokoli: 'kotokoli',
    lamba: 'loso',
    loso: 'loso',
    tem: 'tem',
    bassar: 'bassar',
    nawdem: 'nawdem',
    moba: 'moba',
    fon: 'fon',
  };
  return alias[v] || v;
};
